import { useEditor, EditorContent, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Table } from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import Highlight from '@tiptap/extension-highlight'
import { useEffect, useRef, useCallback, useState } from 'react'

/* ── Toolbar Button ── */
interface ToolbarBtnProps {
  icon: string
  title: string
  active?: boolean
  disabled?: boolean
  onClick: () => void
}

function ToolbarBtn({ icon, title, active, disabled, onClick }: ToolbarBtnProps) {
  return (
    <button
      type="button"
      className={`rte-toolbar__btn${active ? ' rte-toolbar__btn--active' : ''}`}
      title={title}
      disabled={disabled}
      onMouseDown={e => { e.preventDefault(); onClick() }}
    >
      {icon}
    </button>
  )
}

/* ── Heading Dropdown ── */
function HeadingDropdown({ editor }: { editor: Editor }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const current = editor.isActive('heading', { level: 3 }) ? 'H3'
    : editor.isActive('heading', { level: 4 }) ? 'H4'
    : editor.isActive('heading', { level: 5 }) ? 'H5'
    : '¶'

  return (
    <div className="rte-toolbar__dropdown" ref={ref}>
      <button
        type="button"
        className={`rte-toolbar__btn rte-toolbar__dropdown-trigger${current !== '¶' ? ' rte-toolbar__btn--active' : ''}`}
        onMouseDown={e => { e.preventDefault(); setOpen(!open) }}
        title="Encabezados"
      >
        {current} <span className="rte-toolbar__caret">▾</span>
      </button>
      {open && (
        <div className="rte-toolbar__dropdown-menu">
          <button
            type="button"
            className={`rte-toolbar__dropdown-item${current === '¶' ? ' rte-toolbar__dropdown-item--active' : ''}`}
            onMouseDown={e => {
              e.preventDefault()
              editor.chain().focus().setParagraph().run()
              setOpen(false)
            }}
          >
            Párrafo
          </button>
          {([3, 4, 5] as const).map(level => (
            <button
              key={level}
              type="button"
              className={`rte-toolbar__dropdown-item${editor.isActive('heading', { level }) ? ' rte-toolbar__dropdown-item--active' : ''}`}
              onMouseDown={e => {
                e.preventDefault()
                editor.chain().focus().toggleHeading({ level }).run()
                setOpen(false)
              }}
            >
              <span style={{ fontSize: level === 3 ? '1.1rem' : level === 4 ? '0.95rem' : '0.85rem', fontWeight: 700 }}>
                Encabezado {level - 2}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/* ── Link Prompt ── */
function promptLink(editor: Editor) {
  const prev = editor.getAttributes('link').href || ''
  const url = window.prompt('URL del enlace:', prev)
  if (url === null) return
  if (url === '') {
    editor.chain().focus().extendMarkRange('link').unsetLink().run()
  } else {
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }
}

/* ── Table Menu ── */
function TableMenu({ editor }: { editor: Editor }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const isInTable = editor.isActive('table')

  return (
    <div className="rte-toolbar__dropdown" ref={ref}>
      <button
        type="button"
        className={`rte-toolbar__btn${isInTable ? ' rte-toolbar__btn--active' : ''}`}
        onMouseDown={e => { e.preventDefault(); setOpen(!open) }}
        title="Tabla"
      >
        ⊞
      </button>
      {open && (
        <div className="rte-toolbar__dropdown-menu">
          {!isInTable ? (
            <button
              type="button"
              className="rte-toolbar__dropdown-item"
              onMouseDown={e => {
                e.preventDefault()
                editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
                setOpen(false)
              }}
            >
              Insertar tabla 3×3
            </button>
          ) : (
            <>
              <button type="button" className="rte-toolbar__dropdown-item" onMouseDown={e => { e.preventDefault(); editor.chain().focus().addColumnAfter().run(); setOpen(false) }}>
                + Columna después
              </button>
              <button type="button" className="rte-toolbar__dropdown-item" onMouseDown={e => { e.preventDefault(); editor.chain().focus().addRowAfter().run(); setOpen(false) }}>
                + Fila después
              </button>
              <button type="button" className="rte-toolbar__dropdown-item" onMouseDown={e => { e.preventDefault(); editor.chain().focus().deleteColumn().run(); setOpen(false) }}>
                − Eliminar columna
              </button>
              <button type="button" className="rte-toolbar__dropdown-item" onMouseDown={e => { e.preventDefault(); editor.chain().focus().deleteRow().run(); setOpen(false) }}>
                − Eliminar fila
              </button>
              <button type="button" className="rte-toolbar__dropdown-item rte-toolbar__dropdown-item--danger" onMouseDown={e => { e.preventDefault(); editor.chain().focus().deleteTable().run(); setOpen(false) }}>
                ✕ Eliminar tabla
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

/* ── Toolbar ── */
function Toolbar({ editor }: { editor: Editor | null }) {
  if (!editor) return null

  return (
    <div className="rte-toolbar">
      {/* Text format */}
      <ToolbarBtn icon="B" title="Negrita (Ctrl+B)" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} />
      <ToolbarBtn icon="I" title="Cursiva (Ctrl+I)" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} />
      <ToolbarBtn icon="U" title="Subrayado (Ctrl+U)" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} />
      <ToolbarBtn icon="S" title="Tachado" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} />
      <ToolbarBtn icon="H" title="Resaltado" active={editor.isActive('highlight')} onClick={() => editor.chain().focus().toggleHighlight().run()} />

      <div className="rte-toolbar__separator" />

      {/* Headings */}
      <HeadingDropdown editor={editor} />

      <div className="rte-toolbar__separator" />

      {/* Lists */}
      <ToolbarBtn icon="•" title="Lista con viñetas" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} />
      <ToolbarBtn icon="1." title="Lista numerada" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
      <ToolbarBtn icon="❝" title="Cita" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} />

      <div className="rte-toolbar__separator" />

      {/* Alignment */}
      <ToolbarBtn icon="≡" title="Alinear izquierda" active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()} />
      <ToolbarBtn icon="≡" title="Centrar" active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} />
      <ToolbarBtn icon="≡" title="Alinear derecha" active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()} />

      <div className="rte-toolbar__separator" />

      {/* Insert */}
      <ToolbarBtn icon="🔗" title="Insertar enlace" active={editor.isActive('link')} onClick={() => promptLink(editor)} />
      <ToolbarBtn icon="━" title="Línea horizontal" onClick={() => editor.chain().focus().setHorizontalRule().run()} />
      <ToolbarBtn icon="⟨⟩" title="Código" active={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()} />
      <TableMenu editor={editor} />

      <div className="rte-toolbar__separator" />

      {/* History */}
      <ToolbarBtn icon="↩" title="Deshacer (Ctrl+Z)" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()} />
      <ToolbarBtn icon="↪" title="Rehacer (Ctrl+Y)" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()} />
    </div>
  )
}

/* ── Main Component ── */
interface RichTextEditorProps {
  content: string
  onChange: (html: string) => void
  placeholder?: string
  minHeight?: number
}

export default function RichTextEditor({ content, onChange, placeholder = 'Escribe aquí...', minHeight = 200 }: RichTextEditorProps) {
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  // Track whether we're syncing external content to avoid feedback loops
  const isSyncingRef = useRef(false)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [3, 4, 5] },
      }),
      Table.configure({ resizable: false }),
      TableRow,
      TableCell,
      TableHeader,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Placeholder.configure({ placeholder }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Underline,
      Highlight.configure({ multicolor: false }),
    ],
    content,
    onUpdate: ({ editor: e }) => {
      if (isSyncingRef.current) return
      const html = e.getHTML()
      // TipTap returns '<p></p>' for empty content, normalize to ''
      const normalized = html === '<p></p>' ? '' : html
      onChangeRef.current(normalized)
    },
    editorProps: {
      attributes: {
        class: 'rte-content',
        style: `min-height: ${minHeight}px`,
      },
    },
  })

  // Sync external content changes (e.g. undo/redo from parent)
  const syncContent = useCallback((newContent: string) => {
    if (!editor) return
    const currentHtml = editor.getHTML()
    const normalizedCurrent = currentHtml === '<p></p>' ? '' : currentHtml
    if (normalizedCurrent !== newContent) {
      isSyncingRef.current = true
      editor.commands.setContent(newContent || '<p></p>', { emitUpdate: false })
      isSyncingRef.current = false
    }
  }, [editor])

  // Use a ref to track what content the editor currently has
  const lastContentRef = useRef(content)

  useEffect(() => {
    if (content !== lastContentRef.current) {
      lastContentRef.current = content
      syncContent(content)
    }
  }, [content, syncContent])

  return (
    <div className="rte-wrapper">
      <Toolbar editor={editor} />
      <div className="rte-editor">
        <EditorContent editor={editor} />
      </div>
    </div>
  )
}
